import { Button, ChipMetadata, LinkButton, Tooltip } from '@pmi/dsm-react';
import {
  AlertWarningSolidIcon,
  ChevronLeftSolidIcon,
  LocationOutlineIcon,
} from '@pmi/dsm-react/dist/components/icons';
import {
  Dispatch,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { AgreementApiModel } from '../../models/DealModel';
import styles from './selectAgreement.module.scss';
// import { DiscountType } from '../../models/ProductModel';
import { EntryPoint, WizardAction } from '.';
import { isPayByUser } from '../../constants';
import { PMIFormatDate } from '../../helpers';
import usePermissions from '../../hooks/usePermissions';
import { User } from '../../models/UserModels';
import PERMISSIONS from '../../permissions';
import { accountSelector } from '../../store/shared/accountSlice';
import { useGetDealsListQuery } from '../../store/shared/dealsApiSlice';
import {
  dealHasExpired,
  dealHasNoAvailableProducts,
} from '../../utils/dataHelpers';
import { CategoryType } from '../CategoryLabel/models';
import RenderHtml from '../RenderHtml';
import AgreementSearchFilters from './AgreementSearchFilters';
import WizardCategoryLabel from './WizardCategoryLabel';

interface SelectAgreementProps {
  selectDeal: (
    selectedDeal: AgreementApiModel,
    dealsSelectionEnabled: boolean,
  ) => void;
  displayedDeals: AgreementApiModel[];
  setDisplayedDeals: Dispatch<SetStateAction<AgreementApiModel[]>>;
  activeTab: number;
  setActiveTab: Dispatch<SetStateAction<number>>;
  onCancel: () => void;
  entryPoint?: EntryPoint;
  people?: User[];
  action?: WizardAction;
}

interface AgreementCardProps {
  agreement: AgreementApiModel;
  isRestricted: boolean;
  onSelect: (
    selectedDeal: AgreementApiModel,
    dealsSelectionEnabled: boolean,
  ) => void;
}

const AgreementCard = memo(
  ({ agreement, isRestricted, onSelect }: AgreementCardProps) => {
    const isExpired = dealHasExpired(agreement);
    const isSoldOut = !isExpired && dealHasNoAvailableProducts(agreement);
    const isDisabled =
      isExpired || isRestricted || isSoldOut || !agreement.enabled;
    const expirationText = PMIFormatDate(agreement.expiration);
    const isPayByUserDeal =
      agreement.isPayByUser || isPayByUser(agreement.paymentArrangement || '');

    const groupedProducts = useMemo(
      () =>
        Object.entries(
          agreement.products.reduce((acc, product) => {
            if (!acc[product.type]) acc[product.type] = [];
            acc[product.type].push(product);
            return acc;
          }, {} as Record<string, typeof agreement.products>),
        ),
      [agreement.products],
    );

    let tooltipContent: string;
    if (isRestricted) {
      tooltipContent =
        'You do not have permission to remove products from this agreement type.';
    } else if (isExpired) {
      tooltipContent = 'You cannot select any product to an expired Agreement';
    } else {
      tooltipContent = 'This agreement has no available products to assign.';
    }

    return (
      <div className={`${styles.defaultCard} ${styles.cardContent}`}>
        <div className="d-block d-sm-flex justify-content-between">
          <div className="d-block">
            <div className="d-block d-sm-flex align-items-center gap-2 flex-wrap">
              <div
                className={`details-block d-flex d-sm-block ${styles.typography20} ${styles.gray800} fw-bold`}
              >
                <span>{agreement.dealName || agreement.id}</span>
                {isExpired && (
                  <span className="d-inline d-sm-none ms-1">
                    <ChipMetadata
                      icon={<AlertWarningSolidIcon />}
                      text="Expired"
                      variant="red"
                      className={styles.smallIcon}
                    />
                  </span>
                )}
              </div>
              {agreement.dealName && (
                <div
                  className={`details-block ${styles.typography14} ${styles.gray800}`}
                >
                  {agreement.id}
                </div>
              )}
              {isExpired && (
                <span className="d-none d-sm-inline">
                  <ChipMetadata
                    icon={<AlertWarningSolidIcon />}
                    text="Expired"
                    variant="red"
                    className={styles.smallIcon}
                  />
                </span>
              )}
              <div
                className={`d-none d-sm-block ${styles.typography14} ${styles.gray600}`}
              >
                |
              </div>
              <div className="details-block d-flex gap-2">
                <div className="block-label d-flex gap-2">
                  <div>
                    <LocationOutlineIcon fontSize="sm" />
                  </div>
                  <div className={`${styles.typography14} ${styles.gray800}`}>
                    Zone Compatibility:{' '}
                  </div>
                  <div
                    className={`${styles.typography14} ${styles.gray800} fw-bold`}
                  >
                    {agreement.zones.length > 0
                      ? `Zone ${agreement.zones.join(', ')}`
                      : 'All Zones'}
                  </div>
                </div>
              </div>
              <div
                className={`d-none d-sm-block ${styles.typography14} ${styles.gray600}`}
              >
                |
              </div>
              <div className="details-block d-flex gap-2">
                <div
                  className={`block-label ${styles.typography14} ${styles.gray800}`}
                >
                  Payment Arrangement:{' '}
                </div>
                <div
                  className={`${styles.typography14} ${styles.gray800} fw-bold`}
                >
                  {agreement?.paymentArrangement}
                </div>
              </div>
            </div>
            <div className="details-block d-flex align-items-center gap-2">
              <div className={`${styles.typography14} ${styles.gray600}`}>
                {agreement.products.length} Product
                {agreement.products.length > 1 ? 's' : ''}
              </div>
              <div className={`${styles.typography14} ${styles.gray600}`}>
                |
              </div>
              <div className={`${styles.typography14} ${styles.gray600}`}>
                <span>{`Expire${isExpired ? 'd' : 's'} on: `}</span>
                <span>{expirationText}</span>
              </div>
            </div>
          </div>
          <div className="d-block">
            <div className="d-block d-sm-none">
              <Button
                className={styles.btnSelectAgreementMobile}
                size="sm"
                iconAlign="right"
                titleText="Select Agreement"
                variant="primary"
                onClick={() => onSelect(agreement, true)}
                disabled={isDisabled}
              />
            </div>
            <div className="d-none d-sm-block">
              <Tooltip
                type="sm"
                placement="top"
                blockTriggers={!isDisabled}
                content={tooltipContent}
                className="darkTooltip"
              >
                <Button
                  className={styles.btnSelectAgreement}
                  size="sm"
                  iconAlign="right"
                  titleText="Select Agreement"
                  variant="primary"
                  onClick={() => onSelect(agreement, true)}
                  disabled={isDisabled}
                />
              </Tooltip>
            </div>
          </div>
        </div>

        <div
          className={`${styles.divider}  ${styles.dividerSpacing} ${styles.groupedProducts}`}
        />
        {groupedProducts.map(([type, products]) => (
          <div
            key={type}
            className="d-block d-sm-flex gap-2 align-items-start mb-2 mb-sm-0"
          >
            <div className={`${styles.categorySection} d-block d-sm-inline`}>
              {type ? <WizardCategoryLabel type={type as CategoryType} /> : '-'}
            </div>

            <div className="prodCategoryGroup">
              {products.map((product, index) => {
                let quantity: number | string;
                if (isExpired || isDisabled) {
                  quantity = 0;
                } else if (isPayByUserDeal) {
                  quantity = '∞';
                } else {
                  quantity = product.quantityRemaining;
                }
                const quantityLabel = isExpired ? 'Redeemed' : 'Available';
                return (
                  <span
                    key={product.sku}
                    className={`${styles.typography12} ${styles.gray600}`}
                    style={{
                      display: 'inline-block',
                      marginRight: '8px',
                    }}
                  >
                    <span
                      className={`${
                        index === 0 ? styles.gray200 : styles.gray400
                      } me-2 d-none d-sm-inline`}
                    >
                      |
                    </span>
                    <RenderHtml html={product.name || product.sku} tag="span" />{' '}
                    - {quantity} {quantityLabel}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  },
);

export default function SelectAgreement({
  selectDeal,
  displayedDeals,
  setDisplayedDeals,
  activeTab,
  setActiveTab,
  onCancel,
  entryPoint = undefined,
  people = undefined,
  action = undefined,
}: SelectAgreementProps) {
  // hooks
  const { currentAccountId } = useSelector(accountSelector);
  const {
    hasAccess,
    isLoading: permissionLoading,
    restrictedAgreementIds,
  } = usePermissions();

  // queries
  const {
    data: dealList,
    isLoading,
    isError,
  } = useGetDealsListQuery(currentAccountId, { skip: permissionLoading });

  const errorMessage = 'Failed to load agreements';
  useEffect(() => {
    if (isError) reportError(errorMessage);
  }, [isError]);

  const accessibleDeals = useMemo(() => {
    const hasPermission = hasAccess({
      permissions: [PERMISSIONS.ManageDealUsers],
    });
    return hasPermission ? dealList ?? [] : [];
  }, [hasAccess, dealList]);

  useEffect(() => {
    if (accessibleDeals.length === 1) selectDeal(accessibleDeals[0], false);
  }, [accessibleDeals, selectDeal]);

  const onFilteredDealsChange = useCallback(
    (deals: AgreementApiModel[]) => {
      setDisplayedDeals(deals);
    },
    [setDisplayedDeals],
  );

  return (
    <div className="wizard-container">
      <div className="stepper-container my-3">
        <ul className="stepper mb-4">
          <li className="stepper-item stepper-item_active">
            <span className="stepper-point">
              <span className="stepper-title">Select Agreement</span>
              <span className="stepper-number">1</span>
            </span>
          </li>
          <li className="stepper-item">
            <div className="stepper-point">
              <span className="stepper-title">Select Products</span>
              <span className="stepper-number">2</span>
            </div>
          </li>
          {/** if entry point is view user modal don't show step 3 */}
          {entryPoint !== EntryPoint.VIEW_USER_MODAL && !people && (
            <li className="stepper-item">
              <span className="stepper-point">
                <span className="stepper-title">Invite People</span>
                <span className="stepper-number">3</span>
              </span>
            </li>
          )}
        </ul>
      </div>
      <div className={`${styles.selectContainer} wizard-container-inner`}>
        <div className="fixed-content">
          <LinkButton
            size="sm"
            className="wizard-back-btn mb-4"
            icon={ChevronLeftSolidIcon}
            iconAlign="left"
            titleText={`Back to ${
              entryPoint === EntryPoint.VIEW_USER_MODAL ? 'Profile' : 'People'
            } `}
            onClick={() => onCancel()}
          />
          <div>
            <h3>Select Agreement</h3>
            {people && (
              <p className="mt-2">
                {action === 'remove'
                  ? 'Displaying agreements that all of the selected people share in common, select an agreement to continue.'
                  : 'Displaying all compatible agreements for these users.'}
              </p>
            )}
          </div>
          <hr className="mt-3 mb-4" />
          <AgreementSearchFilters
            agreements={accessibleDeals}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            action={action}
            people={people}
            onFilteredDealsChange={onFilteredDealsChange}
          />
        </div>
        <div className="scrollable-content">
          <div>
            {displayedDeals &&
              displayedDeals.length > 0 &&
              displayedDeals.map((agreement) => {
                const isRestricted =
                  action === 'remove' &&
                  restrictedAgreementIds.includes(agreement.id);
                return (
                  <AgreementCard
                    key={agreement.id}
                    agreement={agreement}
                    isRestricted={isRestricted}
                    onSelect={selectDeal}
                  />
                );
              })}
            {isLoading && (
              <Spinner size="sm" className="text-primary d-block m-auto" />
            )}
            {displayedDeals && !isLoading && displayedDeals.length === 0 && (
              <div className={`${styles.typography12} ${styles.gray600} mt-4`}>
                <p>
                  {action === 'remove'
                    ? 'No common agreement found'
                    : 'No agreement found'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
